/**
 * If given an array, just returns the array. If given a non-array, puts the
 * value in a single-item array
 *
 * @example
 * const request: { appId: Maybe.ReadonlyArray<AppId> }
 * for (const appId of pack(request.appId)) {
 *   // Do something
 * }
 */
export function pack<T>(val: T): T extends readonly unknown[] ? T : [T]
export function pack(
  maybeList: Maybe.ReadonlyArray<unknown>,
): readonly unknown[] {
  // NOTE: overload allows us to avoid type parameters which would lead to casting
  return Array.isArray(maybeList) ? maybeList : [maybeList]
}

/**
 * If given an array with a single item, returns that item. Otherwise, returns
 * what was given
 *
 * @example
 * function execute(request: Request, options: Options): Response
 * function execute(request: readonly Request[], options: Options): Response[]
 * function execute(request: Maybe.ReadonlyArray<Request>, options: Options): Maybe.ReadonlyArray<Response> {
 *   return unpack(pack(request).map(request => {
 *     // Do something
 *     return response
 *   }))
 * }
 */
export function unpack<T>(array: readonly T[]): Maybe.ReadonlyArray<T>
export function unpack(
  array: readonly unknown[],
): Maybe.ReadonlyArray<unknown> {
  // NOTE: overload allows us to avoid type parameters which would lead to casting
  return Array.isArray(array) && array.length === 1 ? array[0] : array
}
